/**
 * @symphenia/calc-core — **der Rechenweg der Kalkulation** (AP-37 4.7).
 *
 * Es gibt genau einen Rechenweg, und er wohnt hier: Die Ergebnisleiste beim
 * Tippen im Browser und die Zahl, die der Server speichert, sind dieselbe
 * Rechnung (AP-37 1.2-2). Reine Funktionen, keine Datenbank, keine
 * Abhängigkeiten zur Laufzeit.
 *
 * Gerechnet wird mit **ungerundeten** Zwischenwerten; gerundet werden nur der
 * Einheitspreis (Schritt 3, nach der Rundung der Kalkulation), der Lohnanteil
 * (Schritt 5, auf 0,01) und die Beträge, die als Geld stehen bleiben (GP,
 * Summen, Nachlass — auf den Cent).
 *
 * Prozentwerte kommen als **Prozent** (25 = 25 %), nicht als Anteil.
 */

/** Stand des Rechenwegs — `/health` meldet ihn (Smoke-Test der CI, AP-25-A). */
export const CALC_CORE_VERSION = '0.1.0'

// ─── Begriffe ────────────────────────────────────────────────────────────────

export type CostKind = 'LABOR' | 'MATERIAL' | 'SUBCONTRACT' | 'EQUIPMENT' | 'OTHER'
export type LineCostKind = Exclude<CostKind, 'LABOR'>
export type QuantityBasis = 'PER_UNIT' | 'TOTAL'
export type SurchargeBasis = 'DIRECT' | 'RUNNING'
export type LaborMode = 'COST_PLUS' | 'BILLING_RATE'
export type PositionKind = 'NORMAL' | 'OPTIONAL' | 'ALTERNATIVE' | 'REGIE' | 'SURCHARGE' | 'TEXT'
export type PositionState = 'OPEN' | 'PRICED' | 'MANUAL' | 'GAP_OPEN'

export const COST_KINDS: readonly CostKind[] = ['LABOR', 'MATERIAL', 'SUBCONTRACT', 'EQUIPMENT', 'OTHER']

export type ByCostKind = Record<CostKind, number>

export interface CostLineInput {
  kind: LineCostKind
  /** Menge je Einheit der Position (`PER_UNIT`) oder für die ganze Position (`TOTAL`). */
  quantity: number
  quantityBasis: QuantityBasis
  /** Zuschlag auf die **Menge** in Prozent. */
  wastePercent?: number | null
  /** Einkaufspreis je Preiseinheit. Leer = 0 (Zeile ohne Preis). */
  unitCost?: number | null
  /** Preis gilt für so viele Einheiten (Artikelstamm, AP-28). Vorgabe 1. */
  priceUnit?: number | null
  discountPercent?: number | null
  weightPerUnit?: number | null
}

export interface WorkStepInput {
  phaseKey?: string | null
  /** Leer = Vorgabe-Qualifikation der Kalkulation (4.5). */
  qualificationCode?: string | null
  /** Stunden je Einheit (`PER_UNIT`) oder für die ganze Position (`TOTAL`). */
  hours: number
  quantityBasis: QuantityBasis
  /** Personen — „2 Monteure je 6 h" ist eine Zeile. Vorgabe 1. */
  crewSize?: number | null
  /**
   * Stundensatz von Hand (Nachtrag 6): ersetzt für diesen Schritt den Satz der
   * Qualifikation — je Lohnmodus ein eigener, weil ein Kostensatz kein
   * Verrechnungssatz ist. Leer = Satz der Qualifikation.
   */
  fixedCostRate?: number | null
  fixedBillingRate?: number | null
}

export interface RateInput {
  qualificationCode: string
  costRate?: number | null
  billingRate?: number | null
  isDefault?: boolean
}

export interface SurchargeInput {
  id: string
  name?: string
  percent: number
  appliesTo: readonly CostKind[]
  basis: SurchargeBasis
}

export interface PositionInput {
  id: string
  kind: PositionKind
  quantity: number
  costLines?: readonly CostLineInput[]
  workSteps?: readonly WorkStepInput[]
  /** Abweichende Prozentsätze je Zuschlagszeile (`surchargeId` → Prozent, 4.6). */
  surchargeOverrides?: Readonly<Record<string, number>> | null
  /** EP von Hand — ersetzt den kalkulierten EP (4.7 Schritt 3). */
  fixedUnitPrice?: number | null
  /** `REGIE`: EP = Verrechnungssatz dieser Qualifikation. */
  regieQualificationCode?: string | null
  /** `SURCHARGE`: Prozent auf die Summe der Bezugspositionen. */
  surchargePercent?: number | null
  /** `SURCHARGE`: die Bezugspositionen; leer = alle zählenden Positionen ohne Zuschlagspositionen. */
  surchargeBaseIds?: readonly string[] | null
  /** Bieterlücke offen (Frage ohne Antwort, 4.12). */
  gapOpen?: boolean
  /**
   * AP-37 Detailblatt K 3.4 — „nicht angeboten": kein EP, zählt in keiner Summe,
   * gilt nicht als offen. (Ein EP 0,00 von Hand ist dagegen ein Preis.)
   */
  notOffered?: boolean
}

export interface CalculationInput {
  laborMode: LaborMode
  /** 0,01 · 0,05 · 0,10 · 1,00 */
  roundingStep: number
  discountPercent?: number | null
  rates: readonly RateInput[]
  surcharges: readonly SurchargeInput[]
  positions: readonly PositionInput[]
}

export interface SurchargeEffect {
  surchargeId: string
  /** Wirkung in Euro (ungerundet) — für das Schlussblatt. */
  amount: number
  /** Wirkung je Kostenart. */
  byKind: ByCostKind
}

export interface PositionResult {
  id: string
  /** Stunden gesamt (Stunden × Personen × Menge bei `PER_UNIT`). */
  hours: number
  /** Einzelkosten je Kostenart; Lohn **immer mit Kostensatz**. */
  cost: ByCostKind
  costTotal: number
  /** Wert je Kostenart nach den Aufschlägen: V(c). */
  value: ByCostKind
  /** GPk — Gesamtpreis kalkuliert, ungerundet. */
  priceCalculated: number
  /** EP kalkuliert (gerundet nach der Rundung der Kalkulation). */
  unitPriceCalculated: number
  /** EP, der gilt: von Hand oder kalkuliert. */
  unitPrice: number
  unitPriceLabor: number
  unitPriceOther: number
  /** GP = EP × M, auf den Cent. */
  totalPrice: number
  /** Zählt in die Angebotssumme (nicht Eventual, Wahl, Text). */
  counts: boolean
  state: PositionState
  /** Ein Arbeitsschritt fand keinen Satz — Lohn dafür 0 (`CALCULATION_RATE_MISSING`). */
  rateMissing: boolean
  weight: number
  hoursByPhase: Record<string, number>
  hoursByQualification: Record<string, number>
  surchargeEffects: SurchargeEffect[]
  /** EP von Hand gegen kalkulierten EP. */
  manualDeviation: number | null
  manualDeviationPercent: number | null
}

export interface CalculationTotals {
  /** Einzelkosten der zählenden Positionen je Kostenart (Lohn mit Kostensatz). */
  cost: ByCostKind
  costTotal: number
  /** Wert nach Aufschlägen je Kostenart, zählende Positionen. */
  value: ByCostKind
  surchargeTotal: number
  /** Angebotssumme netto (Σ GP der zählenden Positionen). */
  priceTotal: number
  discountAmount: number
  priceAfterDiscount: number
  /** Deckungsbeitrag = Summe nach Nachlass − Σ Einzelkosten. */
  contributionMargin: number
  contributionMarginPercent: number | null
  hoursTotal: number
  /** Mittellohn = Lohnkosten / Stunden. */
  averageWage: number | null
  weightTotal: number
  hoursByPhase: Record<string, number>
  hoursByQualification: Record<string, number>
  surchargeEffects: SurchargeEffect[]
  /** Rundung und EP von Hand: Σ GP − Σ GPk der zählenden Positionen. */
  roundingAndManual: number
  positionsPriced: number
  positionsOpen: number
  gapsOpen: number
  rateMissing: number
}

export interface CalculationResult {
  positions: PositionResult[]
  byId: Record<string, PositionResult>
  totals: CalculationTotals
}

// ─── Runden ──────────────────────────────────────────────────────────────────

/**
 * Kaufmännisch runden auf einen Schritt (0,01 · 0,05 · 0,10 · 1,00), halb weg
 * von null. Der Zwischenschritt über neun Stellen fängt die Darstellung von
 * Gleitkommazahlen ab (`1,005 / 0,01` ist `100,49999…`).
 */
export function runde(wert: number, schritt = 0.01): number {
  if (!Number.isFinite(wert)) return 0
  const s = schritt > 0 ? schritt : 0.01
  const n = Math.round((Math.abs(wert) / s) * 1e9) / 1e9
  const gerundet = Math.round(n) * s
  // Nochmals auf den Cent, damit 0,05-Schritte keine Rauschstellen tragen.
  const ergebnis = Math.round(gerundet * 100) / 100
  return wert < 0 ? -ergebnis : ergebnis
}

/** Auf den Cent. */
export const aufCent = (wert: number): number => runde(wert, 0.01)

const zahl = (w: number | null | undefined, vorgabe = 0): number =>
  typeof w === 'number' && Number.isFinite(w) ? w : vorgabe

const leer = (): ByCostKind => ({ LABOR: 0, MATERIAL: 0, SUBCONTRACT: 0, EQUIPMENT: 0, OTHER: 0 })

const summe = (werte: ByCostKind): number => COST_KINDS.reduce((s, k) => s + werte[k], 0)

function addiere(ziel: Record<string, number>, schluessel: string, wert: number) {
  ziel[schluessel] = (ziel[schluessel] ?? 0) + wert
}

// ─── Eine Kostenzeile, ein Arbeitsschritt (4.3, 4.4) ─────────────────────────

/** Bedarfsmenge = Menge × Positionsmenge (`PER_UNIT`) bzw. Menge (`TOTAL`). */
export function bedarfsmenge(menge: number, basis: QuantityBasis, positionsmenge: number): number {
  return basis === 'PER_UNIT' ? zahl(menge) * zahl(positionsmenge) : zahl(menge)
}

export interface CostLineResult {
  requiredQuantity: number
  /** Menge mit Verschnitt. */
  quantityWithWaste: number
  cost: number
  weight: number
}

/** Kosten = Bedarfsmenge × (1 + Verschnitt) × Preis / Preiseinheit × (1 − Rabatt). */
export function rechneKostenzeile(zeile: CostLineInput, positionsmenge: number): CostLineResult {
  const bedarf = bedarfsmenge(zeile.quantity, zeile.quantityBasis, positionsmenge)
  const mitVerschnitt = bedarf * (1 + zahl(zeile.wastePercent) / 100)
  const preiseinheit = zahl(zeile.priceUnit, 1) > 0 ? zahl(zeile.priceUnit, 1) : 1
  const kosten = ((mitVerschnitt * zahl(zeile.unitCost)) / preiseinheit) * (1 - zahl(zeile.discountPercent) / 100)
  // Gewicht netto, ohne Verschnitt: Stahlbau und Verzinkerei rechnen mit dem
  // Gewicht, das verbaut wird (Entscheidung B-4).
  return { requiredQuantity: bedarf, quantityWithWaste: mitVerschnitt, cost: kosten, weight: bedarf * zahl(zeile.weightPerUnit) }
}

export interface WorkStepResult {
  hours: number
  qualificationCode: string | null
  costRate: number | null
  billingRate: number | null
  /** Lohnkosten mit Kostensatz. */
  laborCost: number
  /** Lohn mit Verrechnungssatz (nur `BILLING_RATE`). */
  laborBilling: number
  rateMissing: boolean
}

/** Stunden gesamt = Stunden × Personen × (Positionsmenge bei `PER_UNIT`). */
export function rechneArbeitsschritt(
  schritt: WorkStepInput,
  positionsmenge: number,
  saetze: readonly RateInput[],
  lohnmodus: LaborMode,
): WorkStepResult {
  const personen = Math.max(1, Math.round(zahl(schritt.crewSize, 1)))
  const stunden = bedarfsmenge(schritt.hours, schritt.quantityBasis, positionsmenge) * personen
  const satz = satzFuer(saetze, schritt.qualificationCode)
  // Ein Satz von Hand geht dem der Qualifikation vor — und er ist ein Satz: Mit ihm fehlt keiner.
  const kostensatz = vonHand(schritt.fixedCostRate) ?? satz?.costRate ?? null
  const verrechnung = vonHand(schritt.fixedBillingRate) ?? satz?.billingRate ?? null
  const fehlt = lohnmodus === 'BILLING_RATE' ? verrechnung == null : kostensatz == null
  return {
    hours: stunden,
    qualificationCode: satz?.qualificationCode ?? schritt.qualificationCode ?? null,
    costRate: kostensatz,
    billingRate: verrechnung,
    laborCost: stunden * zahl(kostensatz),
    laborBilling: stunden * zahl(verrechnung),
    rateMissing: stunden > 0 && fehlt,
  }
}

/** Ein Satz von Hand gilt nur als Zahl — auch 0,00 ist einer (wie beim EP von Hand). */
const vonHand = (w: number | null | undefined): number | null => (typeof w === 'number' && Number.isFinite(w) ? w : null)

/** Der Satz einer Qualifikation — ohne eigene die Vorgabe der Kalkulation (4.5). */
export function satzFuer(saetze: readonly RateInput[], code: string | null | undefined): RateInput | undefined {
  if (code) {
    const eigener = saetze.find((s) => s.qualificationCode === code)
    if (eigener) return eigener
  }
  return code ? undefined : (saetze.find((s) => s.isDefault) ?? undefined)
}

// ─── Eine Position (4.7 Schritte 1–6) ────────────────────────────────────────

const OHNE_SUMME: readonly PositionKind[] = ['OPTIONAL', 'ALTERNATIVE', 'TEXT']

/** Zählt die Art in die Angebotssumme? Eventual- und Wahlpositionen werden gerechnet, zählen aber nicht. */
export function zaehlt(kind: PositionKind): boolean {
  return !OHNE_SUMME.includes(kind)
}

function leeresErgebnis(p: PositionInput): PositionResult {
  return {
    id: p.id,
    hours: 0,
    cost: leer(),
    costTotal: 0,
    value: leer(),
    priceCalculated: 0,
    unitPriceCalculated: 0,
    unitPrice: 0,
    unitPriceLabor: 0,
    unitPriceOther: 0,
    totalPrice: 0,
    counts: zaehlt(p.kind),
    state: p.kind === 'TEXT' ? 'PRICED' : 'OPEN',
    rateMissing: false,
    weight: 0,
    hoursByPhase: {},
    hoursByQualification: {},
    surchargeEffects: [],
    manualDeviation: null,
    manualDeviationPercent: null,
  }
}

/** EP aus GPk: runden(GPk / M); ohne Menge der GPk selbst (Pauschale). */
function einheitspreis(gpk: number, menge: number, schritt: number): number {
  return menge > 0 ? runde(gpk / menge, schritt) : runde(gpk, schritt)
}

function zustand(p: PositionInput, ep: number): PositionState {
  if (p.gapOpen) return 'GAP_OPEN'
  if (p.kind === 'TEXT') return 'PRICED'
  if (p.fixedUnitPrice != null) return 'MANUAL'
  return ep !== 0 ? 'PRICED' : 'OPEN'
}

/**
 * Rechnet eine Position — ohne `SURCHARGE`, die braucht die übrigen
 * (`rechneKalkulation` erledigt sie im zweiten Durchgang).
 */
export function rechnePosition(
  p: PositionInput,
  eingabe: Pick<CalculationInput, 'laborMode' | 'roundingStep' | 'rates' | 'surcharges'>,
): PositionResult {
  const e = leeresErgebnis(p)
  if (p.kind === 'TEXT') return e
  const M = zahl(p.quantity)

  // ── REGIE: EP = Verrechnungssatz, Menge in Stunden, keine Kostenzeilen ──
  if (p.kind === 'REGIE') {
    const satz = satzFuer(eingabe.rates, p.regieQualificationCode)
    e.hours = M
    e.cost.LABOR = M * zahl(satz?.costRate)
    e.costTotal = e.cost.LABOR
    e.rateMissing = satz?.billingRate == null
    const epKalk = runde(zahl(satz?.billingRate), eingabe.roundingStep)
    e.value.LABOR = M * epKalk
    e.priceCalculated = e.value.LABOR
    return schliesse(e, p, epKalk, eingabe.roundingStep, M)
  }

  const e0 = rechneBlatt(p, eingabe, M, e)
  if (M !== 0) return schliesse(e0, p, einheitspreis(e0.priceCalculated, M, eingabe.roundingStep), eingabe.roundingStep, M)

  // Aufzahlung ohne Menge (AP-37 Nachtrag 1, N1-29): Der EP wird wie für **eine** Einheit
  // gerechnet, der GP bleibt EP × 0 — die Position zeigt ihren Preis, zählt aber nichts.
  // Eine reine Pauschale (nur „gesamt“) rechnet dabei wie bisher: EP = GPk.
  const eins = rechneBlatt(p, eingabe, 1, leeresErgebnis(p))
  const r = schliesse(e0, p, runde(eins.priceCalculated, eingabe.roundingStep), eingabe.roundingStep, 0)
  r.unitPriceLabor = eins.priceCalculated !== 0 ? aufCent((r.unitPrice * eins.value.LABOR) / eins.priceCalculated) : 0
  r.unitPriceOther = aufCent(r.unitPrice - r.unitPriceLabor)
  return r
}

/** Schritte 1 und 2 und der GPk einer Position für die Menge `M` (ohne EP). */
function rechneBlatt(
  p: PositionInput,
  eingabe: Pick<CalculationInput, 'laborMode' | 'roundingStep' | 'rates' | 'surcharges'>,
  M: number,
  e: PositionResult,
): PositionResult {
  // ── Schritt 1: Einzelkosten je Kostenart ──
  for (const zeile of p.costLines ?? []) {
    const r = rechneKostenzeile(zeile, M)
    e.cost[zeile.kind] += r.cost
    e.weight += r.weight
  }
  let lohnVerrechnung = 0
  for (const schritt of p.workSteps ?? []) {
    const r = rechneArbeitsschritt(schritt, M, eingabe.rates, eingabe.laborMode)
    e.hours += r.hours
    e.cost.LABOR += r.laborCost
    lohnVerrechnung += r.laborBilling
    if (r.rateMissing) e.rateMissing = true
    addiere(e.hoursByPhase, schritt.phaseKey ?? '', r.hours)
    addiere(e.hoursByQualification, r.qualificationCode ?? '', r.hours)
  }
  e.costTotal = summe(e.cost)

  // ── Schritt 2: Aufschläge der Reihe nach ──
  const V: ByCostKind = { ...e.cost }
  const billing = eingabe.laborMode === 'BILLING_RATE'
  if (billing) V.LABOR = lohnVerrechnung
  for (const zuschlag of eingabe.surcharges) {
    const prozent = p.surchargeOverrides?.[zuschlag.id] ?? zuschlag.percent
    const p100 = zahl(prozent) / 100
    const wirkung = leer()
    for (const c of zuschlag.appliesTo) {
      // Im Lohnmodus BILLING_RATE enthält der Verrechnungssatz schon alle Zuschläge.
      if (billing && c === 'LABOR') continue
      const delta = zuschlag.basis === 'DIRECT' ? p100 * e.cost[c] : p100 * V[c]
      wirkung[c] += delta
    }
    for (const c of COST_KINDS) V[c] += wirkung[c]
    e.surchargeEffects.push({ surchargeId: zuschlag.id, amount: summe(wirkung), byKind: wirkung })
  }
  e.value = V

  // ── Schritt 3: GPk (der EP folgt in `rechnePosition`) ──
  e.priceCalculated = summe(V)
  return e
}

/** Schritte 3 (EP von Hand), 4 (GP) und 5 (Lohnanteil). */
function schliesse(e: PositionResult, p: PositionInput, epKalk: number, _schritt: number, M: number): PositionResult {
  e.unitPriceCalculated = epKalk
  const vonHand = p.fixedUnitPrice != null && Number.isFinite(p.fixedUnitPrice)
  e.unitPrice = vonHand ? aufCent(p.fixedUnitPrice as number) : epKalk
  if (vonHand) {
    e.manualDeviation = aufCent(e.unitPrice - epKalk)
    e.manualDeviationPercent = epKalk !== 0 ? ((e.unitPrice - epKalk) / epKalk) * 100 : null
  }
  // Schritt 4: GP = EP × M — so rechnet jedes LV und jeder Beleg, nie GPk.
  e.totalPrice = aufCent(e.unitPrice * M)
  // Schritt 5: Lohnanteil im kalkulierten Verhältnis — auch bei EP von Hand.
  e.unitPriceLabor = e.priceCalculated !== 0 ? aufCent((e.unitPrice * e.value.LABOR) / e.priceCalculated) : 0
  e.unitPriceOther = aufCent(e.unitPrice - e.unitPriceLabor)
  e.state = zustand(p, e.unitPrice)
  if (p.notOffered) {
    e.unitPrice = 0
    e.unitPriceLabor = 0
    e.unitPriceOther = 0
    e.totalPrice = 0
    e.counts = false
    e.state = 'PRICED'
  }
  return e
}

// ─── Die ganze Kalkulation (4.7 Schritt 7) ───────────────────────────────────

export function rechneKalkulation(eingabe: CalculationInput): CalculationResult {
  const ergebnisse = new Map<string, PositionResult>()

  // Erster Durchgang: alles außer Zuschlagspositionen.
  for (const p of eingabe.positions) {
    if (p.kind === 'SURCHARGE') continue
    ergebnisse.set(p.id, rechnePosition(p, eingabe))
  }

  // Zweiter Durchgang: Zuschlagspositionen — Prozent auf die Bezugspositionen.
  for (const p of eingabe.positions) {
    if (p.kind !== 'SURCHARGE') continue
    const e = leeresErgebnis(p)
    const basisIds = p.surchargeBaseIds?.length
      ? p.surchargeBaseIds
      : eingabe.positions.filter((x) => x.kind !== 'SURCHARGE' && zaehlt(x.kind)).map((x) => x.id)
    const basis = basisIds.reduce((s, id) => s + (ergebnisse.get(id)?.totalPrice ?? 0), 0)
    e.priceCalculated = (basis * zahl(p.surchargePercent)) / 100
    const M = zahl(p.quantity, 1)
    e.value.OTHER = e.priceCalculated
    ergebnisse.set(p.id, schliesse(e, p, einheitspreis(e.priceCalculated, M, eingabe.roundingStep), eingabe.roundingStep, M))
  }

  const positionen = eingabe.positions.map((p) => ergebnisse.get(p.id) as PositionResult)
  return { positions: positionen, byId: Object.fromEntries(ergebnisse), totals: summen(positionen, eingabe) }
}

function summen(positionen: readonly PositionResult[], eingabe: CalculationInput): CalculationTotals {
  const cost = leer()
  const value = leer()
  const hoursByPhase: Record<string, number> = {}
  const hoursByQualification: Record<string, number> = {}
  const effekte = new Map<string, SurchargeEffect>()
  let priceTotal = 0
  let hours = 0
  let weight = 0
  let gpk = 0
  let priced = 0
  let open = 0
  let gaps = 0
  let rateMissing = 0

  // Zähler (4.10, 4.12): Textpositionen zählen nicht; eine offene Bieterlücke ist
  // weder ausgepreist noch „offen", sondern eine Lücke (Entscheidung H-9).
  const art = new Map(eingabe.positions.map((p) => [p.id, p.kind]))
  for (const r of positionen) {
    if (r.rateMissing) rateMissing++
    if (art.get(r.id) !== 'TEXT') {
      if (r.state === 'GAP_OPEN') gaps++
      else if (r.state === 'OPEN') open++
      else priced++
    }
    if (!r.counts) continue
    for (const c of COST_KINDS) {
      cost[c] += r.cost[c]
      value[c] += r.value[c]
    }
    priceTotal += r.totalPrice
    gpk += r.priceCalculated
    hours += r.hours
    weight += r.weight
    for (const [k, v] of Object.entries(r.hoursByPhase)) addiere(hoursByPhase, k, v)
    for (const [k, v] of Object.entries(r.hoursByQualification)) addiere(hoursByQualification, k, v)
    for (const eff of r.surchargeEffects) {
      const z = effekte.get(eff.surchargeId) ?? { surchargeId: eff.surchargeId, amount: 0, byKind: leer() }
      z.amount += eff.amount
      for (const c of COST_KINDS) z.byKind[c] += eff.byKind[c]
      effekte.set(eff.surchargeId, z)
    }
  }

  priceTotal = aufCent(priceTotal)
  const discountAmount = aufCent((priceTotal * zahl(eingabe.discountPercent)) / 100)
  const priceAfterDiscount = aufCent(priceTotal - discountAmount)
  const costTotal = summe(cost)
  const contributionMargin = aufCent(priceAfterDiscount - costTotal)

  return {
    cost,
    costTotal,
    value,
    surchargeTotal: summe(value) - costTotal,
    priceTotal,
    discountAmount,
    priceAfterDiscount,
    contributionMargin,
    contributionMarginPercent: priceAfterDiscount !== 0 ? (contributionMargin / priceAfterDiscount) * 100 : null,
    hoursTotal: hours,
    averageWage: hours > 0 ? cost.LABOR / hours : null,
    weightTotal: weight,
    hoursByPhase,
    hoursByQualification,
    surchargeEffects: eingabe.surcharges.map(
      (s) => effekte.get(s.id) ?? { surchargeId: s.id, amount: 0, byKind: leer() },
    ),
    roundingAndManual: priceTotal - gpk,
    positionsPriced: priced,
    positionsOpen: open,
    gapsOpen: gaps,
    rateMissing,
  }
}

/**
 * AP-37 Detailblatt K 6.1/6.2 — **effektiver Zuschlagsfaktor je Kostenart**: was aus
 * 1,00 € Einzelkosten nach allen Aufschlägen des Schemas wird (auch `RUNNING`, auch mit
 * Abweichungen einer Position). K7 weist ihn als „Gesamtzuschlag %" je Kostenart aus,
 * K3 als Zeile 20. Im Lohnmodus `BILLING_RATE` trägt der Verrechnungssatz die
 * Zuschläge schon — der Faktor auf Lohn ist dort 1.
 */
export function zuschlagsfaktoren(
  eingabe: Pick<CalculationInput, 'laborMode' | 'surcharges'>,
  overrides?: Readonly<Record<string, number>> | null,
): ByCostKind {
  const V: ByCostKind = { LABOR: 1, MATERIAL: 1, SUBCONTRACT: 1, EQUIPMENT: 1, OTHER: 1 }
  const billing = eingabe.laborMode === 'BILLING_RATE'
  for (const z of eingabe.surcharges) {
    const p100 = zahl(overrides?.[z.id] ?? z.percent) / 100
    const wirkung = leer()
    for (const c of z.appliesTo) {
      if (billing && c === 'LABOR') continue
      wirkung[c] += z.basis === 'DIRECT' ? p100 : p100 * V[c]
    }
    for (const c of COST_KINDS) V[c] += wirkung[c]
  }
  return V
}

/**
 * Der GP einer Position nach Nachlass, anteilig — für den Vertrag zur
 * Nachkalkulation (`CalculationSummaryLine.salesPrice`, AP-37 6.3).
 */
export function gpNachNachlass(gp: number, totals: Pick<CalculationTotals, 'priceTotal' | 'priceAfterDiscount'>): number {
  return totals.priceTotal !== 0 ? (gp * totals.priceAfterDiscount) / totals.priceTotal : gp
}

/**
 * Das Ab-Werk-Schema „Standard" (4.6) — Platzhalter, keine Empfehlung.
 */
export const STANDARD_SCHEMA: readonly Omit<SurchargeInput, 'id'>[] = [
  { name: 'Gemeinkosten Lohn', percent: 25, appliesTo: ['LABOR'], basis: 'DIRECT' },
  { name: 'Gemeinkosten Material', percent: 10, appliesTo: ['MATERIAL', 'SUBCONTRACT', 'EQUIPMENT', 'OTHER'], basis: 'DIRECT' },
  { name: 'Wagnis und Gewinn', percent: 8, appliesTo: ['LABOR', 'MATERIAL', 'SUBCONTRACT', 'EQUIPMENT', 'OTHER'], basis: 'RUNNING' },
]
export * from './rechenweg'
